/* eslint-disable */

import { useState, useContext } from "react";
import { ProductSummary } from "./ProductSummary";
import { plus, pencil, bin } from "@simplybusiness/icons";
import { Image, Segment, Button, Flex, Icon } from "@simplybusiness/mobius";
import { CombinedPaymentContext } from "./CombinedPaymentContext";

import { fetchRequest } from "./UtilsHelper";
import { useBreakpoint } from "@simplybusiness/mobius";
import { DialogueModal } from "./DialogueModal";
import { useTriggerEventTracker } from "./helpers/PersistentPaymentSummaryHelper";

export function SummaryWrapper({
  productName,
  productSummary,
  totalGrossPremium,
  hasSharedLimits,
  insurerLogo,
  policyStartDate,
  expandCover,
  mobileView,
  editURL,
  markedForPurchase,
  onUpdateItem,
  cartItemReference,
  promptInformation = { promptCustomer: false },
  eligibleForRemoval,
  editModalDescription,
  applicationDashboard = false,
  updateApplicationDashboard,
  isOnlyProduct,
  hasPrimaryCover,
}: SummaryWrapperProps) {
  const additionalCover: SummaryWrapperProps["productSummary"] = {};
  const [editQuote, setIsEditQuote] = useState(false);
  const [removeItem, setIsRemovedItem] = useState(false);
  const [addItem, setIsAddedItem] = useState(false);
  const isCombinedPaymentPage = useContext(CombinedPaymentContext);
  const [eventName, setEventName] = useState<string | null>(null);

  useTriggerEventTracker(eventName);

  const productSummaryKeys = Object.keys(productSummary);
  /** re-organize the product and split products and additional cover based on optional key */
  const product = productSummaryKeys.reduce(
    (products, key) => {
      if (!productSummary[key].optional) {
        products[key] = productSummary[key];
      } else {
        additionalCover[key] = productSummary[key];
      }
      return products;
    },
    {} as SummaryWrapperProps["productSummary"],
  );

  const breakpointSize = useBreakpoint([
    { size: "sm", value: 567 },
    { size: "md", value: 568 },
    { size: "lg", value: 812 },
  ]).breakpoint.size;
  const isMobileLandScape = breakpointSize == "md";

  const setButtonStyle = () => {
    return {
      alignSelf: "center",
      padding: isMobileLandScape && "6px",
      fontSize: isMobileLandScape && "var(--font-size-2)",
    };
  };

  const editButton = (
    <Button
      className="mobius-payment-summary__button"
      variant="primary"
      size="sm"
      isDisabled={editQuote}
      onPress={() => {
        try {
          setEventName("go_back_to_quote_selection_clicked");
        } catch (e) {
          console.error("Error in TriggerEventTracker", e);
        }
      }}
    >
      {isCombinedPaymentPage ? (
        <>Edit</>
      ) : (
        <div>
          <Icon icon={pencil} title="Edit product details" />
          {"\u00A0"}Edit
        </div>
      )}
    </Button>
  );

  const toggleItem = async (cartItemReference: string | undefined) => {
    const url = `?step_action=update_cart_item`;
    const payload = JSON.stringify({
      item_id: cartItemReference,
      action: "update",
    });
    const response = await fetchRequest(url, payload);
    onUpdateItem(response, cartItemReference);

    if (response) {
      setIsAddedItem(false);
      setIsRemovedItem(false);
    }
  };

  return (
    <>
      <Segment>
        <Flex className="mobius-payment-summary__summary-wrapper-segment">
          <Image
            className="mobius-payment-summary__styled-image"
            src={insurerLogo}
            alt={"insurerLogo"}
          />
          {!eligibleForRemoval &&
            !promptInformation.promptCustomer &&
            editURL && (
              <DialogueModal
                cancelButtonText={"Cancel, return to checkout"}
                headerText={"Editing your coverage"}
                actionButtonText={"Go back to your quote selection"}
                redirectFunction={() => (window.location.href = editURL)}
                description={editModalDescription || ``}
                button={editButton}
              />
            )}
          {eligibleForRemoval && !promptInformation.promptCustomer ? (
            markedForPurchase ? (
              <Button
                className="mobius-payment-summary__button"
                variant="primary"
                size="sm"
                isLoading={removeItem}
                isDisabled={removeItem}
                onPress={() => {
                  setIsRemovedItem(true);
                  toggleItem(cartItemReference);
                }}
              >
                <div>
                  <Icon icon={bin} title="Remove product from cart" />
                  {"\u00A0"}Remove
                </div>
              </Button>
            ) : (
              <Button
                className="mobius-payment-summary__button"
                variant="primary"
                size="sm"
                isLoading={addItem}
                isDisabled={addItem}
                onPress={() => {
                  setIsAddedItem(true);
                  toggleItem(cartItemReference);
                }}
              >
                <div>
                  <Icon icon={plus} title="Add product to cart" />
                  {"\u00A0"}Add
                </div>
              </Button>
            )
          ) : (
            ""
          )}
        </Flex>
        <div
          className={`mobius-payment-summary__summary-wrapper-div persistent_product_summary ${
            !markedForPurchase && "exclude-for-purchase"
          }`}
        >
          {
            <ProductSummary
              productName={productName}
              product={product}
              additionalCover={additionalCover}
              totalGrossPremium={totalGrossPremium}
              hasSharedLimits={hasSharedLimits}
              mobileView={mobileView}
              policyStartDate={policyStartDate}
              applicationDashboard={applicationDashboard}
              updateApplicationDashboard={updateApplicationDashboard}
              isOnlyProduct={isOnlyProduct}
              hasPrimaryCover={hasPrimaryCover}
            ></ProductSummary>
          }
        </div>
      </Segment>
    </>
  );
}

interface SummaryWrapperProps {
  productSummary: Record<string, any>;
  insurerLogo: string;
  totalGrossPremium?: string;
  hasSharedLimits?: boolean;
  policyStartDate?: string;
  expandCover?: boolean;
  mobileView: boolean;
  editURL?: string;
  markedForPurchase?: boolean;
  onUpdateItem: (response: any, cartItemReference: string | undefined) => void;
  cartItemReference?: string;
  promptInformation?: {
    promptCustomer?: boolean;
    message?: string;
    actionUrl?: string;
    buttonText?: string;
    type?: string;
  };
  eligibleForRemoval?: boolean;
  editModalDescription?: string;
  applicationDashboard?: boolean;
  isOnlyProduct?: boolean;
  hasPrimaryCover?: boolean;
  updateApplicationDashboard?: (response: unknown) => void;
  productName: string;
}
